import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { StateService } from '@uirouter/angular';
import { Subject, takeUntil } from 'rxjs';
import { SavedSearchesDataService } from '../../data-access/widgets/saved-searches-data.service';

@Component({
  selector: 'app-saved-searches-widget',
  template: `
    <app-dashboard-card title="Saved Project/Contract Award Searches">
      <div class="saved-search-toolbar" style="display:flex; gap:8px; margin-bottom:8px;">
        <button #renameModeButton mat-stroked-button class="saved-search-button" (click)="toggleRenameMode()" [attr.aria-label]="isRenameMode ? 'Finish renaming saved searches' : 'Rename saved searches'">{{ isRenameMode ? 'Done' : 'Rename' }}</button>
        <button #deleteModeButton mat-stroked-button class="saved-search-button saved-search-button--warn" (click)="toggleDeleteMode()" [attr.aria-label]="isDeleteMode ? 'Cancel deleting saved searches' : 'Delete saved searches'">{{ isDeleteMode ? 'Cancel Delete' : 'Delete' }}</button>
        <button mat-stroked-button class="saved-search-button saved-search-button--warn" *ngIf="isDeleteMode" (click)="confirmDelete()" [disabled]="deleteSelection.size === 0" aria-label="Confirm delete selected saved searches">Confirm</button>
      </div>
      <div *ngIf="message" class="message">{{ message }}</div>
      <div *ngIf="items.length === 0">No saved searches</div>
      <div *ngIf="items.length > 0" class="dashboard-scroll-list" [class.dashboard-scroll-list--delete-mode]="isDeleteMode" role="table" aria-label="Saved searches">
        <div class="saved-search-header" role="row">
          <div class="saved-search-col saved-search-col--select" *ngIf="isDeleteMode"></div>
          <div class="saved-search-col saved-search-col--subject" role="columnheader">Subject</div>
          <div class="saved-search-col saved-search-col--name" role="columnheader">Saved Search</div>
        </div>
        <div
          class="saved-search-row"
          [class.saved-search-row--clickable]="!isDeleteMode && !isRenameMode"
          *ngFor="let item of items; trackBy: trackBySavedSearch; let rowIndex = index"
          (click)="select(item)"
          (keyup.enter)="select(item)"
          [tabindex]="isDeleteMode || isRenameMode ? -1 : 0"
          role="row"
          [attr.aria-rowindex]="rowIndex + 2">
          <div class="saved-search-col saved-search-col--select" *ngIf="isDeleteMode">
            <mat-checkbox [checked]="deleteSelection.has(item.value)" (click)="$event.stopPropagation()" (change)="toggleDelete(item)" [attr.aria-label]="'Select ' + (item.label || item.searchName || item.value) + ' for deletion'"></mat-checkbox>
          </div>
          <div class="saved-search-col saved-search-col--subject">{{ getSubjectLabel(item) }}</div>
          <div class="saved-search-col saved-search-col--name">
            <ng-container *ngIf="!isRenameMode">{{ item.label || item.searchName || item.value }}</ng-container>
            <input *ngIf="isRenameMode" #renameInput matInput [value]="item.label" (click)="$event.stopPropagation()" (change)="item.label = $any($event.target).value" (blur)="rename(item)" (keydown.enter)="rename(item); exitRenameMode(); $event.preventDefault()" (keydown.escape)="cancelRename(item); exitRenameMode(); $event.preventDefault()" [attr.aria-label]="'Rename saved search ' + (item.originalLabel || item.label || item.value)" />
          </div>
        </div>
      </div>
    </app-dashboard-card>
  `,
  styles: [
    `.message { margin: 8px 0; }`,
    `.saved-search-button { border-color: var(--mat-sys-outline, var(--sys-outline)); color: var(--mat-sys-on-surface, var(--sys-on-surface)); }`,
    `.saved-search-button:hover { background: var(--mat-sys-surface-container-highest, var(--sys-surface-container-highest)); color: var(--mat-sys-on-surface, var(--sys-on-surface)); }`,
    `.saved-search-button--warn { border-color: var(--mat-sys-error, var(--sys-error)); color: var(--mat-sys-on-surface, var(--sys-on-surface)); }`,
    `.saved-search-button--warn:hover { background: var(--mat-sys-error-container, var(--sys-error-container)); color: var(--mat-sys-on-error-container, var(--sys-on-error-container)); }`,
    `.dashboard-scroll-list { display: block; min-height: 0; max-height: 280px; overflow-y: auto; overflow-x: hidden; border: none; border-radius: 6px; background: var(--table-row-bg); }`,
    `.saved-search-header, .saved-search-row { display: grid; grid-template-columns: 110px 1fr; align-items: center; }`,
    `.saved-search-col { padding: 6px 8px; min-height: 34px; display: flex; align-items: center; color: var(--table-heading); }`,
    `.saved-search-col--name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }`,
    `.saved-search-header { position: sticky; top: 0; z-index: 2; background: var(--table-row-alternate-bg); border-bottom: none; font-weight: 600; color: var(--table-heading); }`,
    `.saved-search-row { border-bottom: none; }`,
    `.saved-search-row:nth-child(odd) { background: var(--table-row-bg); }`,
    `.saved-search-row:nth-child(even) { background: var(--table-row-alternate-bg); }`,
    `.saved-search-row--clickable { cursor: pointer; }`,
    `.saved-search-row--clickable:hover { background: var(--table-row-hover); color: var(--table-heading); }`,
    `.saved-search-row--clickable:hover .saved-search-col { color: var(--table-heading); }`,
    `.saved-search-col--subject { color: var(--table-heading); }`,
    `.saved-search-col input[matInput] { width: 100%; }`,
    `.saved-search-col--select { width: 46px; justify-content: center; padding-left: 4px; padding-right: 4px; }`,
    `.dashboard-scroll-list--delete-mode .saved-search-header,
     .dashboard-scroll-list--delete-mode .saved-search-row { grid-template-columns: 46px 110px 1fr; }`,
    `@media (max-width: 768px) { .dashboard-scroll-list { max-height: 220px; } }`
  ]
})
export class SavedSearchesWidgetComponent implements OnInit, OnDestroy {
  items: any[] = [];
  isDeleteMode = false;
  isRenameMode = false;
  deleteSelection = new Set<string>();
  message = '';
  @ViewChild('renameModeButton') private renameModeButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('deleteModeButton') private deleteModeButton?: ElementRef<HTMLButtonElement>;
  @ViewChildren('renameInput') private renameInputs?: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren(MatCheckbox) private deleteCheckboxes?: QueryList<MatCheckbox>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dataService: SavedSearchesDataService,
    private readonly stateService: StateService
  ) { }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.dataService.getSavedSearches()
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => this.items = (Array.isArray(items) ? items : []).map(item => ({ ...item, originalLabel: item.label })));
  }

  select(item: any): void {
    if (this.isDeleteMode || this.isRenameMode) {
      return;
    }

    const guid = String(item?.value || '').trim();
    if (!guid) {
      return;
    }

    const name = String(item?.label || '').trim();

    const state = this.getStateForSearchSubject(item?.searchSubject);
    this.stateService.go(state, { guid, name: encodeURIComponent(name) });
  }

  private getStateForSearchSubject(searchSubject: unknown): string {
    const normalized = String(searchSubject || '').trim().toLowerCase();

    if (normalized === 'contract award' || normalized === 'contractawards' || normalized.includes('contract')) {
      return 'search.contractAwardSummary';
    }

    return 'search.projectSummary';
  }

  getSubjectLabel(item: any): string {
    const normalized = String(item?.searchSubject || '').trim().toLowerCase();
    if (normalized.includes('contract')) {
      return 'Contract Award';
    }

    if (normalized.includes('project')) {
      return 'Project';
    }

    return 'Project';
  }

  toggleDeleteMode(): void {
    this.isDeleteMode = !this.isDeleteMode;
    this.isRenameMode = false;
    this.message = '';
    if (!this.isDeleteMode) {
      this.deleteSelection.clear();
      this.focusDeleteModeButton();
      return;
    }

    this.focusFirstDeleteCheckbox();
  }

  toggleRenameMode(): void {
    this.isRenameMode = !this.isRenameMode;
    this.isDeleteMode = false;
    this.deleteSelection.clear();
    this.message = '';

    if (!this.isRenameMode) {
      this.focusRenameModeButton();
      return;
    }

    this.focusFirstRenameInput();
  }

  toggleDelete(item: any): void {
    const key = item.value;
    if (!key) return;

    if (this.deleteSelection.has(key)) {
      this.deleteSelection.delete(key);
    } else {
      this.deleteSelection.add(key);
    }
  }

  confirmDelete(): void {
    const selected = this.items.filter(item => this.deleteSelection.has(item.value));
    if (!selected.length) {
      this.message = 'No saved searches selected.';
      return;
    }

    this.message = '';
    let deletedCount = 0;
    let remaining = selected.length;
    let hadError = false;
    selected.forEach(item => {
      this.dataService.deleteSavedSearch(item.value).subscribe({
        next: () => {
          this.items = this.items.filter(existing => existing.value !== item.value);
          this.deleteSelection.delete(item.value);
          deletedCount++;
          this.message = `${deletedCount} saved search${deletedCount === 1 ? '' : 'es'} deleted.`;
        },
        error: () => {
          hadError = true;
          this.message = 'Unable to delete one or more saved searches.';
        },
        complete: () => {
          remaining--;
          if (remaining === 0 && !hadError) {
            this.isDeleteMode = false;
            this.focusDeleteModeButton();
          }
        }
      });
    });
  }

  rename(item: any): void {
    const label = String(item?.label || '').trim();
    if (!label || !item?.value || label === item?.originalLabel) {
      item.label = item.originalLabel;
      return;
    }

    const duplicate = this.items.filter(x => String(x.label || '').toLowerCase() === label.toLowerCase()).length > 1;
    if (duplicate) {
      item.label = item.originalLabel;
      this.message = 'Saved search name must be unique.';
      return;
    }

    const payload = { ...item, label };
    this.dataService.renameSavedSearch(item.value, payload).subscribe({
      next: () => {
        item.originalLabel = label;
        this.message = 'Saved search renamed.';
      },
      error: () => {
        item.label = item.originalLabel;
        this.message = 'Unable to rename saved search.';
      }
    });
  }

  cancelRename(item: any): void {
    item.label = item?.originalLabel;
  }

  exitRenameMode(): void {
    this.isRenameMode = false;
    this.focusRenameModeButton();
  }

  private focusFirstRenameInput(): void {
    setTimeout(() => this.renameInputs?.first?.nativeElement?.focus());
  }

  private focusFirstDeleteCheckbox(): void {
    setTimeout(() => this.deleteCheckboxes?.first?.focus());
  }

  private focusRenameModeButton(): void {
    setTimeout(() => this.renameModeButton?.nativeElement?.focus());
  }

  private focusDeleteModeButton(): void {
    setTimeout(() => this.deleteModeButton?.nativeElement?.focus());
  }

  trackBySavedSearch(_: number, item: any): string {
    return String(item?.value || '');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
